import { CheckCircle2, XCircle, RotateCcw, Sparkles, Home } from "lucide-react";

function evaluate(pct) {
  if (pct === 100) return { label: "Perfekcyjnie! Masz to opanowane.", tone: "success" };
  if (pct >= 80) return { label: "Świetny wynik! Opanowałeś większość materiału.", tone: "success" };
  if (pct >= 60) return { label: "Nieźle. Warto powtórzyć słabe punkty.", tone: "info" };
  if (pct >= 40) return { label: "Materiał wymaga jeszcze pracy — nie poddawaj się!", tone: "warn" };
  return { label: "Zacznij od powtórki teorii i spróbuj jeszcze raz.", tone: "warn" };
}

export default function ResultsView({ questions, answers, onRepeatWeak, onNewTopic, subtitle }) {
  const correctCount = questions.reduce(
    (acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0),
    0
  );
  const total = questions.length;
  const pct = Math.round((correctCount / total) * 100);
  const wrong = questions
    .map((q, i) => ({ q, i, user: answers[i] }))
    .filter((x) => x.user !== x.q.correct_index);
  const evaluation = evaluate(pct);

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein" data-testid="results-view">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
        <Sparkles className="w-3.5 h-3.5" /> {subtitle || "Wyniki quizu"}
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
        Twój wynik
      </h1>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="bg-[#FEF9C3] border border-yellow-200 rounded-3xl p-6 sm:p-8">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Punkty</div>
          <div className="mt-2 text-5xl sm:text-6xl font-extrabold tracking-tight text-slate-900" data-testid="results-score">
            {correctCount}<span className="text-slate-400">/{total}</span>
          </div>
          <div className="mt-1 text-lg font-semibold text-slate-700" data-testid="results-percentage">{pct}%</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 flex flex-col justify-center">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Ocena</div>
          <div className="mt-2 text-lg sm:text-xl font-bold text-slate-900" data-testid="results-evaluation">
            {evaluation.label}
          </div>
        </div>
      </div>

      {wrong.length > 0 && (
        <section className="mt-10" data-testid="wrong-questions-section">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Do powtórki</h2>
          <p className="text-slate-500 mt-1 text-sm">Pytania, w których się pomyliłeś:</p>
          <div className="mt-4 space-y-4">
            {wrong.map(({ q, i, user }) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`wrong-question-${i}`}>
                <div className="flex items-center gap-2 text-xs font-bold uppercase text-red-500 mb-2">
                  <XCircle className="w-4 h-4" /> Pytanie {i + 1}
                </div>
                <div className="font-bold text-slate-900">{q.question}</div>
                <div className="mt-3 text-sm space-y-1.5">
                  <div className="flex gap-2 items-start">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">Twoja odpowiedź: <b className="text-slate-800">{user != null ? q.options[user] : "—"}</b></span>
                  </div>
                  <div className="flex gap-2 items-start">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">Poprawna: <b className="text-slate-900">{q.options[q.correct_index]}</b></span>
                  </div>
                </div>
                {q.explanation && (
                  <div className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-xl p-3 leading-relaxed">
                    {q.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        {wrong.length > 0 && (
          <button
            data-testid="repeat-weak-areas-button"
            onClick={() => onRepeatWeak(wrong.map((w) => ({
              question: w.q.question,
              correct_answer: w.q.options[w.q.correct_index],
              user_answer: w.user != null ? w.q.options[w.user] : "brak",
            })))}
            className="inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold text-base px-6 py-3.5 rounded-2xl btn-yellow-glow"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={2.5} />
            Powtórz to, czego nie umiałem
          </button>
        )}
        <button
          data-testid="new-topic-button"
          onClick={onNewTopic}
          className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border-2 border-slate-200 hover:border-slate-900 font-bold text-base px-6 py-3.5 rounded-2xl transition-colors"
        >
          <Home className="w-4 h-4" strokeWidth={2.5} />
          Nowy temat
        </button>
      </div>
    </div>
  );
}
